import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions: string[];
  mfaEnabled?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  requiresPasswordChange: boolean;
  requiresMFASetup: boolean;
  lastActivity: number | null;
  sessionExpiresAt: number | null;

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  setRequiresPasswordChange: (requires: boolean) => void;
  setRequiresMFASetup: (requires: boolean) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  hasRole: (...roles: string[]) => boolean;
  updateActivity: () => void;
  isSessionValid: () => boolean;
  checkAndRefreshSession: () => boolean;
}

// Session timeout in milliseconds (8 hours to match backend JWT)
const SESSION_TIMEOUT = 8 * 60 * 60 * 1000;
// Activity timeout - logout after 30 minutes of inactivity
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      requiresPasswordChange: false,
      requiresMFASetup: false,
      lastActivity: null,
      sessionExpiresAt: null,

      setAuth: (user, accessToken, refreshToken) => {
        const now = Date.now();
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          lastActivity: now,
          sessionExpiresAt: now + SESSION_TIMEOUT,
        });
      },

      setTokens: (accessToken, refreshToken) => {
        const now = Date.now();
        set((state) => ({
          accessToken,
          refreshToken: refreshToken ?? state.refreshToken,
          lastActivity: now,
          sessionExpiresAt: now + SESSION_TIMEOUT,
        }));
      },

      setRequiresPasswordChange: (requires) => {
        set({ requiresPasswordChange: requires });
      },

      setRequiresMFASetup: (requires) => {
        set({ requiresMFASetup: requires });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          requiresPasswordChange: false,
          requiresMFASetup: false,
          lastActivity: null,
          sessionExpiresAt: null,
        });
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'superadmin') return true;
        return user.permissions?.includes(permission) ?? false;
      },

      hasAnyPermission: (...permissions) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'superadmin') return true;
        return permissions.some((p) => user.permissions?.includes(p));
      },

      hasRole: (...roles) => {
        const { user } = get();
        if (!user) return false;
        return roles.includes(user.role);
      },

      updateActivity: () => {
        const { isAuthenticated } = get();
        if (isAuthenticated) {
          set({ lastActivity: Date.now() });
        }
      },

      isSessionValid: () => {
        const { isAuthenticated, lastActivity, sessionExpiresAt } = get();
        if (!isAuthenticated) return false;

        const now = Date.now();

        // Check if session has expired
        if (sessionExpiresAt && now > sessionExpiresAt) {
          return false;
        }

        // Check for inactivity timeout
        if (lastActivity && (now - lastActivity) > INACTIVITY_TIMEOUT) {
          return false;
        }

        return true;
      },

      checkAndRefreshSession: () => {
        const { isSessionValid, logout } = get();
        if (!isSessionValid()) {
          logout();
          return false;
        }
        return true;
      },
    }),
    {
      name: 'lucas-tvs-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
        sessionExpiresAt: state.sessionExpiresAt,
      }),
    }
  )
);

// Setup activity listeners for user interactions
if (typeof window !== 'undefined') {
  const updateActivity = () => {
    const store = useAuthStore.getState();
    if (store.isAuthenticated) {
      store.updateActivity();
    }
  };

  // Track user activity
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });

  // Periodic session check (every minute)
  setInterval(() => {
    const store = useAuthStore.getState();
    if (store.isAuthenticated && !store.isSessionValid()) {
      store.logout();
      window.location.href = '/login?expired=true';
    }
  }, 60000);

  // Handle visibility change - check session when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const store = useAuthStore.getState();
      if (store.isAuthenticated && !store.isSessionValid()) {
        store.logout();
        window.location.href = '/login?expired=true';
      }
    }
  });

  // Handle storage events for multi-tab sync
  window.addEventListener('storage', (event) => {
    if (event.key === 'lucas-tvs-auth') {
      const store = useAuthStore.getState();
      // If storage was cleared (logout in another tab), redirect to login
      if (!event.newValue) {
        store.logout();
        window.location.href = '/login';
      }
    }
  });
}

